<template>
  <div
    class="bg-surface-50 dark:bg-surface-700/40 rounded-xl mt-4 origin-center mx-auto flex flex-col w-full"
  >
    <div class="rounded-t-xl py-2 px-3 flex justify-between bg-primary">
      <div class="max-w-full overflow-hidden">
        <div class="flex items-center gap-2">
          <span>
            <img
              :src="iconUrl || fallbackIconUrl"
              class="h-5 w-5 svg-color"
              @error="onImageLoadError"
            />
          </span>
          <span class="font-semibold text-surface-0">{{ title ?? fallbackTitle }}</span>
        </div>
        <p
          v-if="description || fallbackDescription"
          class="ma- text-surface-0 text-sm truncate"
        >
          {{ description ?? fallbackDescription }}
        </p>
        <div class="flex gap-2 gap-y-0 flex-wrap font-medium">
          <StatusTag
            v-if="!hideQuickActions && hasQuickActions"
            label="Quick actions available"
            icon="pi-check"
          />
          <StatusTag
            v-if="postedDate"
            :label="`Posted: ${postedDate}`"
            icon="pi-share-alt"
            success
          />
          <StatusTag
            v-if="scheduledDate"
            :label="`Scheduled: ${scheduledDate}`"
            icon="pi-clock"
            success
          />
        </div>
      </div>

      <slot name="headingRight" />
    </div>

    <div class="p-2 sm:p-4 grow flex flex-col">
      <div class="relative flex flex-col grow">
        <slot v-if="loading" name="loader">
          <p>{{ $t("loading.wait") }}</p>
        </slot>
        <slot v-else-if="hasError" name="error">
          <p>{{ $t("errors.tryAgain") }}</p>
        </slot>
        <slot v-else />

        <div
          v-if="isUpdating"
          id="quick-task-updating-overlay"
          class="absolute inset-0 z-10 bg-surface-900/40 flex gap-2 flex-col items-center justify-center"
        >
          <SpinIcon class="text-3xl" />
          <p class="text-surface-0">{{ $t("buttonsSavingChanges") }}</p>
        </div>
      </div>

      <div v-if="!hideQuickActions" class="grid grid-cols-3 gap-4 mt-6">
        <div class="col-span-3 flex items-center gap-2">
          <i class="pi pi-sparkles text-primary" />
          <p class="font-medium">{{ $t("quickActions") }}</p>
        </div>
        <button
          v-for="action in quickActions"
          :id="action.id"
          :key="action.key"
          :disabled="isUpdating || !action.enabled"
          class="p-2 bg-surface-0 dark:bg-surface-700 rounded-md flex flex-col items-center justify-center hover:shadow-lg duration-200 disabled:cursor-not-allowed disabled:opacity-40"
          :data-gtm-action="action.gtmAction"
          @click="onQuickAction(action.key, $event)"
        >
          <span
            class="flex items-center justify-center h-10 w-10 rounded-md text-surface-0"
            :class="action.background"
          >
            <component
              :is="action.icon"
              v-if="typeof action.icon !== 'string'"
              class="h-6 w-6"
            />
            <i v-else :class="`text-lg ${action.icon}`" />
          </span>
          <p class="font-medium mt-2">{{ action.label }}</p>
          <span class="text-sm ma-text-secondary">
            {{ action.description }}
          </span>
        </button>
      </div>

      <div
        v-if="
          canPostOrSchedule && (features?.post?.enabled || features?.schedule?.enabled)
        "
        class="grid grid-cols-1 @sm:grid-cols-2 gap-4 mt-8 not-supports-cq:sm:grid-cols-2"
      >
        <AppButton
          v-if="features?.schedule?.enabled"
          id="quick-task-result-post-later"
          variant="secondary"
          :label="$t('postLater')"
          icon="pi pi-clock"
          :disabled="isUpdating"
          class="[&_span]:flex-0 [&_span]:whitespace-nowrap"
          :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_schedule_post"
          @click="handlePostOrSchedule('schedule')"
        />

        <AppButton
          v-if="features?.post?.enabled"
          id="quick-task-result-post-now"
          :label="$t('postNow')"
          icon="pi pi-send"
          :disabled="isUpdating"
          class="[&_span]:flex-0 [&_span]:whitespace-nowrap"
          :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_post_now"
          @click="handlePostOrSchedule('post')"
        />
      </div>

      <div v-if="scheduledDate || postedDate" class="mt-6 text-center">
        <p class="text-sm ma-text-secondary mt-1">
          <span v-if="scheduledDate && postedDate">{{
            $t("scheduledAndPostedAt", {
              scheduleTime: scheduledDate,
              postTime: postedDate,
            })
          }}</span>
          <span v-else-if="postedDate">{{ $t("postedOn", { date: postedDate }) }}</span>
          <span v-else-if="scheduledDate">{{
            $t("scheduledFor", { date: scheduledDate })
          }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import QuillPenIcon from "@/components/icons/QuillPenIcon.vue";
import SpinIcon from "@/components/icons/SpinIcon.vue";
import type { QuickTaskActionPayload } from "@/composables/quickTask";
import type { EditableContentType } from "@/utils/content";
import { format } from "date-fns";
import { useI18n } from "vue3-i18n";

const props = withDefaults(
  defineProps<{
    title?: string;
    iconUrl?: string;
    contentId?: string;
    contentType?: EditableContentType;
    dataPath?: string;
    taskType?: string;
    hideDescription?: boolean;
    hideQuickActions?: boolean;
    canPostOrSchedule?: boolean;
    description?: string;
    loading?: boolean;
    hasError?: boolean;
    postedAt?: string;
    scheduledAt?: string;
  }>(),
  {
    canPostOrSchedule: true,
  }
);

const contentUpdateList = useContentUpdateList();
const { contentFeatures } = useQuickTask();
const { t } = useI18n();
const {
  title: fallbackTitle,
  iconUrl: fallbackIconUrl,
  onImageLoadError,
  description: fallbackDescription,
  taskConfig,
} = useSingleTaskConfigDetails(() => props.taskType || "");

const features = computed(() => {
  if (!props.contentId) return null;
  return contentFeatures.value?.[props.contentId] || null;
});

const isUpdating = computed(() => {
  if (!props.contentId || !contentUpdateList.value.length) return false;
  return contentUpdateList.value.includes(props.contentId);
});

// 2 Oct 2025, 3:45 PM
const postedDate = computed(() => {
  if (!props.postedAt) return null;
  try {
    const date = new Date(props.postedAt);
    return isNaN(date.getTime()) ? null : format(date, "d MMM yyyy, h:mm aa");
  } catch {
    return null;
  }
});

const scheduledDate = computed(() => {
  if (!props.scheduledAt) return null;
  try {
    const date = new Date(props.scheduledAt);
    return isNaN(date.getTime()) ? null : format(date, "d MMM yyyy, h:mm aa");
  } catch {
    return null;
  }
});

function StatusTag(props: { label: string; icon: string; success?: boolean }) {
  return h(
    "div",
    {
      class: `text-sm flex items-center gap-2 rounded-full w-max p-0.5 pr-1 mt-2 ${props["success"] ? "bg-green-500" : "bg-surface-0/20"}`,
    },
    [
      h(
        "span",
        {
          class: `h-4 w-4 bg-surface-0 rounded-full flex items-center justify-center ${props["success"] ? "text-green-500" : "text-primary"}`,
        },
        [h("i", { class: `pi ${props.icon} text-xs` })]
      ),
      h("span", { class: "text-surface-0 text-xs capitalize" }, props.label),
    ]
  );
}

function handlePostOrSchedule(action: "post" | "schedule") {
  if (action === "post") {
    features.value?.post?.callback?.();
  } else if (action === "schedule") {
    features.value?.schedule?.callback?.();
  }
}

function onQuickAction(type: QuickTaskActionPayload["type"], event?: MouseEvent) {
  let payload: QuickTaskActionPayload;
  if (!props.contentId) return;

  if (type === "edit") {
    if (!props.dataPath || !props.contentType) return;
    payload = {
      type: "edit",
      contentId: props.contentId,
      contentType: props.contentType,
      dataPath: props.dataPath,
    };
  } else if (type === "regenerate") {
    if (!props.taskType || !props.dataPath) return;
    if (!taskConfig.value?.tone) return;
    payload = {
      type: "regenerate",
      contentId: props.contentId,
      tone: taskConfig.value.tone,
      dataPath: props.dataPath,
    };
  } else {
    payload = {
      type,
      contentId: props.contentId,
    };
  }
  handleV2QuickTaskAction(payload, event?.target);
}

const quickActions = computed(() => {
  return [
    {
      label: t("buttons.edit"),
      description: "Edit, add images, or regenerate",
      background: "bg-surface-500",
      key: "edit",
      id: "quick-task-action-edit",
      gtmAction: analyticsEvents.QUICK_TASK.quick_task_result_action_edit,
      icon: QuillPenIcon,
      enabled:
        taskConfig.value?.isContentEditable && !!props.dataPath && !!props.contentId,
    },
    {
      label: t("addMedia"),
      description: "Quickly add images or videos to this content",
      background: "bg-primary",
      key: "add-media",
      icon: "pi pi-image",
      id: "quick-task-action-add-media",
      gtmAction: analyticsEvents.QUICK_TASK.quick_task_action_add_media,
      enabled: taskConfig.value?.canEditImages && !!props.contentId,
    },
    {
      label: t("regenerate"),
      description: "Regenerate in a different tone",
      background: "bg-orange-500",
      key: "regenerate",
      icon: "pi pi-sparkles",
      id: "quick-task-action-regenerate",
      gtmAction: analyticsEvents.QUICK_TASK.quick_task_action_regenerate,
      enabled:
        taskConfig.value?.isContentRegenerate &&
        !!taskConfig.value?.tone &&
        !!props.dataPath &&
        !!props.contentId,
    },
  ] as const;
});

const hasQuickActions = computed(() => {
  return quickActions.value.some(action => action.enabled);
});
</script>

<style scoped></style>
